import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Drawer, DrawerContent, DrawerOverlay, DrawerPortal } from "@/components/ui/drawer";
import { Drawer as DrawerPrimitive } from "vaul";
import { useCart } from "@/store/cart";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag, X } from "lucide-react";
import { formatCurrency } from "@/lib/format";
import { useSettings } from "@/hooks/useSettings";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

export const CartDrawer = () => {
  const { items, isOpen, setOpen, updateQty, removeItem, subtotal } = useCart();
  const settings = useSettings();
  const symbol = settings.currency_symbol || "৳";
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const handleCheckout = () => {
    setOpen(false);
    navigate(user ? "/checkout" : "/auth?redirect=/checkout");
  };

  const Header = () => (
    <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b shrink-0">
      <div>
        <h2 className="font-display text-xl font-semibold leading-tight">Your cart</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          {items.length} {items.length === 1 ? "item" : "items"}
        </p>
      </div>
      <button
        onClick={() => setOpen(false)}
        aria-label="Close cart"
        className="grid h-10 w-10 place-items-center rounded-full bg-secondary/80 hover:bg-secondary transition-all active:scale-90"
      >
        <X className="h-4.5 w-4.5" />
      </button>
    </div>
  );

  const Body = () =>
    items.length === 0 ? (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
        <ShoppingBag className="h-12 w-12 text-muted-foreground/40" />
        <p className="text-muted-foreground">Your cart is empty</p>
        <Button onClick={() => setOpen(false)} variant="outline">Continue shopping</Button>
      </div>
    ) : (
      <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-3">
        {items.map((item) => (
          <div key={item.cartId} className="flex gap-3 rounded-xl bg-secondary/30 p-3">
            <div className="h-20 w-20 rounded-lg bg-secondary overflow-hidden shrink-0">
              {item.image_url && <img src={item.image_url} alt={item.name} className="h-full w-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm line-clamp-2">{item.name}</p>
              {(item.size || item.color) && (
                <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                  {item.size && (
                    <span>Size: <span className="font-medium text-foreground">{item.size}</span></span>
                  )}
                  {item.color && (
                    <span className="inline-flex items-center gap-1">
                      Color:
                      <span
                        className="inline-block h-3 w-3 rounded-full ring-1 ring-foreground/15"
                        style={{ background: item.color }}
                      />
                      <span className="font-medium text-foreground">{item.color}</span>
                    </span>
                  )}
                </p>
              )}
              <p className="text-sm font-semibold text-primary mt-1">{formatCurrency(item.price, symbol)}</p>
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={() => updateQty(item.cartId, item.quantity - 1)}
                  className="grid h-7 w-7 place-items-center rounded-full bg-background hover:bg-background/70 border transition-transform active:scale-90"
                  disabled={item.quantity <= 1}
                >
                  <Minus className="h-3 w-3" />
                </button>
                <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                <button
                  onClick={() => updateQty(item.cartId, item.quantity + 1)}
                  className="grid h-7 w-7 place-items-center rounded-full bg-background hover:bg-background/70 border transition-transform active:scale-90"
                  disabled={item.quantity >= item.stock}
                >
                  <Plus className="h-3 w-3" />
                </button>
                <button
                  onClick={() => removeItem(item.cartId)}
                  className="ml-auto grid h-7 w-7 place-items-center rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                  aria-label="Remove"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );

  const Footer = () =>
    items.length > 0 && (
      <div className="border-t bg-background/95 backdrop-blur px-5 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] space-y-3 shrink-0">
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-muted-foreground">Subtotal</span>
          <span className="font-display text-xl font-semibold">{formatCurrency(subtotal(), symbol)}</span>
        </div>
        <p className="text-[11px] text-muted-foreground -mt-1">Shipping calculated at checkout</p>
        <Button variant="hero" size="lg" className="w-full" onClick={handleCheckout}>
          Checkout
        </Button>
      </div>
    );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={setOpen}>
        <DrawerPortal>
          <DrawerOverlay className="bg-black/60 backdrop-blur-sm" />
          <DrawerPrimitive.Content
            className={cn(
              "fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-2xl border bg-background",
              "h-[82vh] max-h-[82vh]"
            )}
          >
            <DrawerPrimitive.Title className="sr-only">Your cart</DrawerPrimitive.Title>
            <div className="mx-auto mt-2 mb-1 h-1.5 w-12 rounded-full bg-muted shrink-0" />
            <Header />
            <Body />
            <Footer />
          </DrawerPrimitive.Content>
        </DrawerPortal>
      </Drawer>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={setOpen}>
      <SheetContent className="flex w-full flex-col sm:max-w-md p-0 [&>button]:hidden">
        <Header />
        <Body />
        <Footer />
      </SheetContent>
    </Sheet>
  );
};
